import {
  boolean,
  customType,
  index,
  integer,
  jsonb,
  numeric,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** Read-only mirror of a Postgres tsvector column. Drizzle has no first-class
 *  tsvector builder; this is a thin `customType` so `jobs.searchVector` types
 *  as `unknown` for any code that selects `jobs.*` (rare — the search route
 *  matches against it entirely in raw `sql` via to_tsquery/plainto_tsquery,
 *  never through the Drizzle query builder). Declared WITHOUT
 *  `.generatedAlwaysAs()` on purpose: the actual GENERATED ALWAYS ... STORED
 *  expression lives ONLY in docs/design/w1-10-ats-search-neon.sql, so
 *  Drizzle never attempts to emit or reconcile the generation expression
 *  (drizzle-kit is not run on this branch; the SQL file is the single
 *  source of truth — same posture the `bucket` column below documents). */
const tsvector = customType<{ data: unknown }>({
  dataType() {
    return 'tsvector';
  },
});

export const jobs = pgTable(
  'jobs',
  {
    id: id(),
    userId: text('user_id').notNull(),
    canonicalUrl: text('canonical_url').notNull().unique(),
    source: varchar('source', { length: 32 }).notNull(),
    company: varchar('company', { length: 200 }).notNull(),
    title: varchar('title', { length: 200 }).notNull(),
    location: varchar('location', { length: 120 }),
    workMode: varchar('work_mode', { length: 24 }),
    employmentType: varchar('employment_type', { length: 24 }),
    seniority: varchar('seniority', { length: 32 }),
    salaryMinAud: integer('salary_min_aud'),
    salaryMaxAud: integer('salary_max_aud'),
    description: text('description'),
    requirements: jsonb('requirements').$type<string[]>(),
    sponsorshipMentioned: boolean('sponsorship_mentioned'),
    selectionCriteriaMode: boolean('selection_criteria_mode').default(false).notNull(),
    /** LANE W2.1-BUCKETS (extends W1-4): the country-agnostic SIX-bucket lane —
     *  'home' | 'relocate' | 'bridge' | 'dream' | 'pass' | 'unsorted'
     *  (lib/bucketJob.ts, JobBucket). Computed on score (independent of which
     *  scenario is scoring it — bucket is a property of the job's own facts,
     *  not the viewing lens) and persisted so the jobs-list read can group +
     *  sort by it without recomputing per request. Nullable: a job scored
     *  before this column existed, or ingested with autoScore:false and never
     *  scored, carries bucket = null — read exactly like 'unsorted' (never a
     *  migration backfill — nothing-unrecoverable). 'pass' is written ONLY by
     *  the user-override endpoint (PATCH /jobs/:id/bucket) — the auto scorer
     *  (bucketJob(), AutoJobBucket) structurally cannot produce it. Additive
     *  (rule #11) — mirrored via SQL, never a migration. */
    bucket: varchar('bucket', { length: 16 }),
    /** LANE W2.1-BUCKETS — 'auto' | 'user'. Marks WHO last set `bucket`:
     *  'auto' (the default, including every pre-existing row read via
     *  COALESCE at the SQL layer) means bucketJob()'s auto-scorer owns it and
     *  a future re-score may freely overwrite it; 'user' means the person set
     *  it explicitly via PATCH /jobs/:id/bucket and it is now TERMINAL —
     *  scoreJobUnderScenarios (lib/scenarios.ts) checks this column FIRST and
     *  skips the auto-bucket entirely once it reads 'user' (the
     *  corrections-are-terminal pattern: a user's own sort always wins over
     *  a re-derive). Additive (rule #11), default 'auto' so no existing row's
     *  behaviour changes. */
    bucketSource: varchar('bucket_source', { length: 8 }).notNull().default('auto'),
    /** BOARD-TRUTH FIX (owner report: a multi-location "Nearby" card showing
     *  a foreign city as its face) — the SPECIFIC `;`-delimited segment of
     *  `location` that justified a 'home'/'relocate' bucket (bucketJob.ts's
     *  BucketDecision.matchedLocation), so the card can show THAT segment
     *  instead of the raw, potentially foreign-city-leading joined string.
     *  null for every other bucket (bridge/dream/unsorted/pass make no
     *  "you're near this" promise — no false-face problem to correct) and for
     *  a single-segment location (nothing to disambiguate). Written
     *  alongside `bucket` on the SAME auto-score / terminal-override
     *  lifecycle (never independently stale relative to the bucket it
     *  explains). Additive (rule #11) — mirrored via SQL, never a migration. */
    resolvedLocation: varchar('resolved_location', { length: 160 }),
    discoveredAt: jsonb('discovered_at'),
    closedAt: jsonb('closed_at'),
    raw: jsonb('raw'),
    /** W1-10(b) full-JD index: GENERATED ALWAYS ... STORED tsvector over
     *  title(A)/company(B)/description(C) — see docs/design/
     *  w1-10-ats-search-neon.sql for the generation expression + the
     *  jobs_search_vector_idx GIN index it also creates. Nullable in the
     *  type only because Drizzle can't express "generated, never null after
     *  backfill" — Postgres backfills every existing row the moment the
     *  column is added, so in practice this is never null once the SQL file
     *  has been applied. Never written by application code (Postgres alone
     *  computes it) — no INSERT/UPDATE in this codebase should ever set it. */
    searchVector: tsvector('search_vector'),
    /** W1-11(e) freshness as first-class data: stamped every time this
     *  posting's data was last CONFIRMED LIVE — the initial ingest AND every
     *  subsequent registry-amortized poll that re-observed it (lib/
     *  registryPoll.ts). Distinct from `discoveredAt` (an immutable jsonb
     *  record of how/when this row was FIRST discovered) and from
     *  `createdAt`/`updatedAt` (touched by ANY field edit, e.g. a user's
     *  manual correction, not specifically a live re-fetch). Nullable: a job
     *  ingested before this column existed, or one that has never been
     *  through an amortized poll (paste-import, one-off /ingest), carries
     *  fetchedAt: null — read as "freshness unknown," never coerced to a
     *  fabricated recency. Search/inbox reads serve this as the row's "as
     *  of" (recency is a fact; unstated freshness is invented freshness). */
    fetchedAt: timestamp('fetched_at', { withTimezone: true }),
    /** D-2 test-data flag (docs/BUILD-PLAN-2026-07-04.md, Reserved call 1): the
     *  FINE, per-row half — stamped `true` only when the ACTING user at write
     *  time carried `users.account_kind IN ('demo','qa')` (threaded from
     *  auth/session into the ingest path — see jobs.routes.ts POST /ingest and
     *  lib/discovery.ts's ingestCandidateForUser — never re-derived here).
     *  Defaults `false` so every pre-existing row reads as real data (NO
     *  INVENTION — nothing here retroactively reclassifies a row that
     *  predates this column). The ONE sanctioned read path
     *  (lib/testDataWall.ts's visibleTo helper) excludes `is_test_data=true`
     *  rows from a REAL viewer's shared/pooled reads; demo/qa viewers see
     *  everything. This is the wall that stops a demo user's seeded/invented
     *  jobs from ever reaching a real user's search or inbox. */
    isTestData: boolean('is_test_data').notNull().default(false),
    /** QA #40 (structured-ingestion) — the posting's OWN publish date, when a
     *  STRUCTURED source hands one cleanly (adzuna `created`, a Workday
     *  postedOn we can parse to a real instant). NEVER derived, guessed, or
     *  back-filled from a fetch time: a source that gives no parseable posting
     *  date carries postedAt = null (freshness unknown), read exactly like a
     *  null fetchedAt — the "unstated recency is invented recency" stance
     *  (jobs.ts fetchedAt's own doc comment), applied to publish date. Additive
     *  (rule #11), mirrored via docs/design/b1-pipeline-neon.sql. Distinct from
     *  fetchedAt (when WE last confirmed it live) and discoveredAt (how/when WE
     *  first saw it): this is when the EMPLOYER posted it. */
    postedAt: timestamp('posted_at', { withTimezone: true }),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('jobs_user_idx').on(t.userId),
    sourceIdx: index('jobs_source_idx').on(t.source),
    companyIdx: index('jobs_company_idx').on(t.company),
    bucketIdx: index('jobs_bucket_idx').on(t.userId, t.bucket),
  }),
);

export const jobExtractions = pgTable('job_extractions', {
  id: id(),
  jobId: text('job_id').notNull(),
  extractionTier: varchar('extraction_tier', { length: 16 }).notNull(),
  jsonLd: jsonb('json_ld'),
  rawHtml: text('raw_html'),
  structuredFields: jsonb('structured_fields'),
  ...timestamps,
});

export const jobScores = pgTable(
  'job_scores',
  {
    id: id(),
    jobId: text('job_id').notNull(),
    /** Which search scenario this score was computed under (geography + work
     *  authorization context). Nullable for legacy scores predating scenarios. */
    scenarioId: text('scenario_id'),
    rubricVersion: varchar('rubric_version', { length: 16 }).notNull(),
    fitScore: numeric('fit_score', { precision: 5, scale: 2 }).notNull(),
    sponsorLikelihood: numeric('sponsor_likelihood', { precision: 4, scale: 3 }),
    reasoning: text('reasoning'),
    gaps: jsonb('gaps').$type<string[]>(),
    strengths: jsonb('strengths').$type<string[]>(),
    radarAxes: jsonb('radar_axes').$type<Record<string, number>>(),
    /** THE JOBS LAKE (B2+M3, docs/design/jobs-lake-design-2026-07-04.md §1.3):
     *  a score is inherently a per-user, per-scenario fact (it already carries
     *  scenarioId), so it belongs beside the per-user link (jobs_lake.ts's
     *  userJobs), not the shared listing. Additive + nullable — `jobId` is
     *  NEVER dropped (rule #11); this column is populated ALONGSIDE it once a
     *  score is written against the new 3-table shape (jobsLake.ts's ingest
     *  path), while every pre-existing row (and every row scored through the
     *  untouched `jobs` table path) simply carries userJobId: null — read as
     *  "this score belongs to the OLD jobs.id shape," never coerced or
     *  backfilled implicitly. The design doc's §5 Step 2 backfill populates
     *  this column explicitly, as a one-time, idempotent, additive pass. */
    userJobId: uuid('user_job_id'),
    /** COMBINE PLAN G1 (docs/design/multi-user-same-job-combine-plan-2026-07-04.md
     *  §9, orchestrator gap G1 — "a score must re-enter its loop when its
     *  inputs change," the no-one-shot-wonders law): when Phase 1's
     *  combineDetails() materially enriches the canonical listing this score
     *  was computed against (salary appears, description grows — see
     *  combineDetails's own MATERIAL_DELTA_FIELDS list), every linked score
     *  gets stamped with WHEN the enrichment happened, so the UI can show an
     *  honest "scored before new info arrived" state and OFFER a bounded
     *  re-score (scoreListingForLinkedUsers, jobsLake.ts — the existing
     *  primitive). NEVER auto-triggers a silent rewrite of `fitScore` itself
     *  — antithesis law, human-in-the-loop; this column is a MARKER, not a
     *  write to the score. Null = never flagged stale (the common case: no
     *  combine has touched this listing since the score was computed, or the
     *  combine only filled fields the field-list doesn't consider material). */
    staleSinceCombineAt: timestamp('stale_since_combine_at', { withTimezone: true }),
    ...timestamps,
  },
  (t) => ({
    jobIdx: index('job_scores_job_idx').on(t.jobId),
    jobScenarioIdx: index('job_scores_job_scenario_idx').on(t.jobId, t.scenarioId),
    userJobIdx: index('job_scores_user_job_idx').on(t.userJobId),
  }),
);

export const salaryIntel = pgTable('salary_intel', {
  id: id(),
  company: varchar('company', { length: 200 }).notNull(),
  level: varchar('level', { length: 32 }),
  location: varchar('location', { length: 120 }),
  baseMinAud: integer('base_min_aud'),
  baseMaxAud: integer('base_max_aud'),
  totalMinAud: integer('total_min_aud'),
  totalMaxAud: integer('total_max_aud'),
  source: varchar('source', { length: 32 }),
  sampleSize: integer('sample_size'),
  lastVerifiedAt: jsonb('last_verified_at'),
  notes: text('notes'),
  ...timestamps,
});

export const extractorProfiles = pgTable('extractor_profiles', {
  id: id(),
  boardName: varchar('board_name', { length: 64 }).notNull().unique(),
  yamlConfig: text('yaml_config').notNull(),
  enabled: boolean('enabled').default(true).notNull(),
  notes: text('notes'),
  ...timestamps,
});
